/**
 * ============================================================================
 * DisqualifyDialog — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: dual-sig + ≥200 char rationale gate to disqualify a supplier
 *          (SUP-DISQ-001). The server enforces the rationale minimum and
 *          "distinct signers"; we mirror both in the UI for fast feedback.
 *
 * A11Y: live counter announced via aria-live="polite"; the submit button is
 *  disabled until both signers and the rationale length all check out.
 * ============================================================================ */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './SupplierDetail.module.css';
import { useDisqualifyMutation } from './useDisqualifyMutation';

const RATIONALE_MIN = 200;

export interface DisqualifyDialogProps {
  supplierId: string;
  supplierName: string;
  onClose: () => void;
  onDisqualified: () => void;
}

export function DisqualifyDialog({
  supplierId,
  supplierName,
  onClose,
  onDisqualified,
}: DisqualifyDialogProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLTextAreaElement | null>(null);

  const disqualifyM = useDisqualifyMutation();
  const [rationale, setRationale] = useState('');
  const [sigA, setSigA] = useState('');
  const [sigB, setSigB] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rationaleLen = rationale.trim().length;
  const sigOk =
    sigA.trim() !== '' && sigB.trim() !== '' && sigA.trim() !== sigB.trim();
  const canSubmit = rationaleLen >= RATIONALE_MIN && sigOk;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (rationaleLen < RATIONALE_MIN) {
      setError(
        t('supplier.detail.disqualify.errors.rationaleShort', {
          defaultValue:
            'Rationale must be at least {{min}} characters (currently {{len}}).',
          min: nf.format(RATIONALE_MIN),
          len: nf.format(rationaleLen),
        }),
      );
      return;
    }
    if (!sigOk) {
      setError(
        t('supplier.detail.disqualify.errors.dualSign', {
          defaultValue:
            'Two distinct signature bundle ids are required (one per signer).',
        }),
      );
      return;
    }
    try {
      await disqualifyM.mutateAsync({
        supplierId,
        rationale: rationale.trim(),
        signature_bundle_id_a: sigA.trim(),
        signature_bundle_id_b: sigB.trim(),
      });
      onDisqualified();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('supplier.detail.disqualify.errors.serverFailed', {
              defaultValue: 'Server rejected the disqualification.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('supplier.detail.disqualify.title', {
              defaultValue: 'Disqualify supplier',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('supplier.detail.disqualify.subtitle', {
              defaultValue:
                'Disqualifying {{name}} is high-blast-radius — it blocks purchase orders and triggers requalification of dependent products. Per SUP-DISQ-001 this action requires two distinct signers and a rationale of at least 200 characters.',
              name: supplierName,
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.dualSigWarn} role="note">
            <strong>{supplierName}</strong>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="sup-disq-rat" className={dialogStyles.label}>
              {t('supplier.detail.disqualify.rationale', {
                defaultValue: 'Rationale',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="sup-disq-rat"
              ref={firstRef}
              className={styles.dialogTextarea}
              value={rationale}
              rows={6}
              onChange={(e) => setRationale(e.target.value)}
              aria-describedby="sup-disq-counter"
              required
            />
            <div
              id="sup-disq-counter"
              className={styles.charCounter}
              aria-live="polite"
            >
              {t('supplier.detail.disqualify.counter', {
                defaultValue: '{{len}} / {{min}} characters',
                len: nf.format(rationaleLen),
                min: nf.format(RATIONALE_MIN),
              })}
            </div>
          </div>

          <div className={styles.dualSigGrid}>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-disq-a" className={dialogStyles.label}>
                {t('supplier.detail.disqualify.sigA', {
                  defaultValue: 'Signature bundle id — SIGNER A',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="sup-disq-a"
                className={dialogStyles.input}
                value={sigA}
                onChange={(e) => setSigA(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-disq-b" className={dialogStyles.label}>
                {t('supplier.detail.disqualify.sigB', {
                  defaultValue: 'Signature bundle id — SIGNER B',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="sup-disq-b"
                className={dialogStyles.input}
                value={sigB}
                onChange={(e) => setSigB(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={disqualifyM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={disqualifyM.isPending}
              disabled={!canSubmit}
            >
              {t('supplier.detail.disqualify.submit', {
                defaultValue: 'Disqualify',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
